import React from "react";
import { DndContext } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { VStack } from "@astryxdesign/core";
import {
  SortableItem,
  previewLabel,
  type UseDragAndDropLogicReturn,
  type DragHandleListeners,
  type DragHandleAttributes,
} from "../../hooks/useDragAndDropLogic";
import NavRail from "./NavRail";
import NavRailItem from "./NavRailItem";
import InsertZone from "./InsertZone";
import type { IScriptTemplate, IScriptTemplateSection, IScriptTemplateQuestion } from "../../types/iscripttemplate";

export interface DraggableRailProps {
  /**
   * The script template data (sections and questions).
   */
  values: IScriptTemplate;

  /**
   * Drag-and-drop logic orchestrated by useDragAndDropLogic hook.
   */
  dragLogic: UseDragAndDropLogicReturn;

  /**
   * Function to generate sortable ID for a section.
   * Allows caller to define ID scheme (e.g., "section:" prefix).
   */
  sectionSortableId: (section: IScriptTemplateSection) => string;

  /**
   * Function to generate sortable ID for a question.
   * Allows caller to define ID scheme (e.g., "question:" prefix).
   */
  questionSortableId: (question: IScriptTemplateQuestion) => string;

  /**
   * Called when the user deletes a section via the NavRailItem's delete button.
   * @param sectionIndex - Index of the section to delete
   */
  onDeleteSection: (sectionIndex: number) => void;

  /**
   * Called when the user deletes a question via the NavRailItem's delete button.
   * @param sectionIndex - Index of the section containing the question
   * @param questionIndex - Index of the question within the section
   */
  onDeleteQuestion: (sectionIndex: number, questionIndex: number) => void;

  /**
   * Called when the user inserts a new question at the specified location.
   * @param sectionIndex - Index of the section to insert into
   * @param questionIndex - Index position where the question should be inserted
   */
  onInsertQuestion: (sectionIndex: number, questionIndex: number) => void;

  /**
   * Called when the user inserts a new section at the specified location.
   * @param sectionIndex - Index of the section before the split
   * @param questionIndex - Index position within that section to split at
   */
  onInsertSection: (sectionIndex: number, questionIndex: number) => void;

  /**
   * If true, the rail is displayed in read-only mode (no drag handles, no delete buttons).
   * @default false
   */
  readonly?: boolean;
}

/**
 * Draggable navigation rail for script sections and questions.
 *
 * Renders sections and questions with drag-and-drop reordering support,
 * optional delete buttons, and insert zones for adding new items.
 * Uses dnd-kit for drag-and-drop with custom collision detection and cross-section moves.
 *
 * This component is purely presentational — it requires fully configured drag logic
 * passed via the `dragLogic` prop from `useDragAndDropLogic`.
 */
export const DraggableRail: React.FC<DraggableRailProps> = ({
  values,
  dragLogic,
  sectionSortableId,
  questionSortableId,
  onDeleteSection,
  onDeleteQuestion,
  onInsertQuestion,
  onInsertSection,
  readonly = false,
}) => {
  if (readonly) {
    // Read-only mode: no drag-and-drop, no insert zones
    return (
      <NavRail>
        <VStack gap={1}>
          {values.scriptTemplateSections.map((section) => (
            <VStack gap={0.5} key={section.clientId}>
              <NavRailItem
                label={previewLabel(section.name, "Untitled section")}
                onClick={() => dragLogic.scrollToSection(section.clientId)}
              />
              <VStack gap={0.5}>
                {section.scriptTemplateQuestions.map((question) => (
                  <NavRailItem
                    key={question.clientId}
                    label={previewLabel(question.body, "Untitled question")}
                    isIndented
                    onClick={() => dragLogic.scrollToQuestion(question.clientId)}
                  />
                ))}
              </VStack>
            </VStack>
          ))}
        </VStack>
      </NavRail>
    );
  }

  // Editable mode with drag-and-drop and insert zones
  return (
    <NavRail>
      <DndContext
        sensors={dragLogic.sensors}
        collisionDetection={dragLogic.collisionDetection}
        onDragStart={dragLogic.onDragStart}
        onDragOver={dragLogic.onDragOver}
        onDragEnd={dragLogic.onDragEnd}
        onDragCancel={dragLogic.onDragCancel}
      >
        <SortableContext
          items={values.scriptTemplateSections.map((section) => sectionSortableId(section))}
          strategy={verticalListSortingStrategy}
        >
          {/* InterVStack gap is 0 — InsertZone is a fixed 24px, in-flow element,
              so it carries the rail's entire inter-row spacing itself. */}
          <VStack gap={0}>
            {values.scriptTemplateSections.map((section, i) => (
              <SortableItem id={sectionSortableId(section)} key={section.clientId}>
                {(sectionListeners: DragHandleListeners, sectionAttributes: DragHandleAttributes) => (
                  <VStack gap={0}>
                    <NavRailItem
                      label={previewLabel(section.name, "Untitled section")}
                      dragHandleProps={{ ...sectionListeners, ...sectionAttributes }}
                      onClick={() => dragLogic.scrollToSection(section.clientId)}
                      onDelete={() => onDeleteSection(i)}
                      deleteLabel="Delete section"
                    />
                    <InsertZone
                      variant="compact"
                      isDisabled={dragLogic.isDragging}
                      onInsertQuestion={() => onInsertQuestion(i, 0)}
                      onInsertSection={() => onInsertSection(i, 0)}
                    />
                    <SortableContext
                      items={section.scriptTemplateQuestions.map((question) =>
                        questionSortableId(question),
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      {section.scriptTemplateQuestions.map((question, j) => (
                        <React.Fragment key={question.clientId}>
                          <SortableItem id={questionSortableId(question)}>
                            {(questionListeners: DragHandleListeners, questionAttributes: DragHandleAttributes) => (
                              <NavRailItem
                                label={previewLabel(question.body, "Untitled question")}
                                isIndented
                                dragHandleProps={{ ...questionListeners, ...questionAttributes }}
                                onClick={() => dragLogic.scrollToQuestion(question.clientId)}
                                onDelete={() => onDeleteQuestion(i, j)}
                                deleteLabel="Delete question"
                              />
                            )}
                          </SortableItem>
                          <InsertZone
                            variant="compact"
                            isDisabled={dragLogic.isDragging}
                            onInsertQuestion={() => onInsertQuestion(i, j + 1)}
                            onInsertSection={() => onInsertSection(i, j + 1)}
                          />
                        </React.Fragment>
                      ))}
                    </SortableContext>
                  </VStack>
                )}
              </SortableItem>
            ))}
          </VStack>
        </SortableContext>
      </DndContext>
    </NavRail>
  );
};

export default DraggableRail;
